import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/daf8h8jep053ijub1l3g";

type Status = "idle" | "loading" | "success" | "error";

const TOPICS = ["职场瓜", "校园瓜", "饭圈瓜", "邻里瓜", "社会瓜", "其他类型"];

export default function Submit() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    const form = event.currentTarget;
    const fd = new FormData(form);

    // 蜜罐检测：命中则假装成功，不真正提交
    const honeypot = (fd.get("website_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim()) {
        payload.append(key, value);
      }
    });

    setStatus("loading");
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = parsed?.meta as Record<string, unknown> | undefined;
      const code = (parsed?.code as string) ?? "";
      const serverMsg =
        (typeof meta?.message === "string" && meta.message) ||
        (typeof parsed?.message === "string" && (parsed.message as string)) ||
        (typeof meta?.detail === "string" && meta.detail) ||
        responseText;

      if (res.ok && code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        const lower = String(serverMsg).toLowerCase();
        setFormError(lower.includes("spam") ? "提交被拦截，请检查后重试。" : String(serverMsg));
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，提交失败，请稍后再试。");
      setStatus("error");
    }
  };

  return (
    <section id="submit" className="scroll-mt-20 bg-primary-50/60 py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-5 lg:gap-14">
          {/* 左侧说明 */}
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-700">
              <i className="ri-send-plane-fill"></i> 立即投稿
            </span>
            <h2 className="mt-4 font-display text-3xl text-foreground-900 md:text-4xl">
              把你手里的瓜，放心交上来
            </h2>
            <p className="mt-4 leading-7 text-foreground-700">
              提交前请再对一遍格式与字数要求，确认信息真实、隐私已打码。填好下方表单，我们会在
              <strong className="font-bold text-foreground-900"> 1–3 个工作日 </strong>
              内完成审核并给你反馈。
            </p>

            <ul className="mt-7 space-y-4">
              {[
                { icon: "ri-lock-2-line", text: "投稿内容仅用于内容审核与发布，不对外公开你的身份信息。" },
                { icon: "ri-shield-star-line", text: "实名、匿名均可投稿，尊重你的隐私意愿。" },
                { icon: "ri-money-cny-circle-line", text: "过审并获推荐的优质瓜料，将按规则结算稿酬。" },
              ].map((item) => (
                <li key={item.text} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                    <i className={item.icon}></i>
                  </span>
                  <p className="pt-1 text-sm leading-6 text-foreground-700">{item.text}</p>
                </li>
              ))}
            </ul>

            <div className="mt-8 rounded-2xl border border-dashed border-primary-300 bg-background-50 p-5">
              <p className="flex items-start gap-2 text-sm leading-6 text-foreground-700">
                <i className="ri-information-line mt-0.5 text-primary-500"></i>
                被打回的投稿不会消失。我们会给出具体修改建议，你按意见调整后重新提交即可，不必从头再写。
              </p>
            </div>
          </div>

          {/* 右侧表单 */}
          <div className="lg:col-span-3">
            <div className="rounded-3xl border border-background-200 bg-background-50 p-6 md:p-8">
              {status === "success" ? (
                <div className="flex flex-col items-center py-16 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-3xl text-accent-600">
                    <i className="ri-checkbox-circle-fill"></i>
                  </span>
                  <h3 className="mt-5 text-xl font-bold text-foreground-900">投稿已收到！</h3>
                  <p className="mt-2 max-w-sm text-sm leading-6 text-foreground-700">
                    感谢你的瓜料。我们会尽快审核，结果将通过你留下的联系方式通知，请留意查收。
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-add-line"></i> 再投一篇
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  {/* 蜜罐字段 */}
                  <div className="hp-zone" aria-hidden="true">
                    <label>
                      网站备用
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
                    </label>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="nickname" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        署名 / 昵称
                      </label>
                      <input
                        id="nickname"
                        name="nickname"
                        type="text"
                        required
                        placeholder="如何称呼你"
                        className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        联系方式（邮箱）
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="用于接收审核结果"
                        className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="title" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      投稿标题 <span className="text-foreground-400">（20 字以内）</span>
                    </label>
                    <input
                      id="title"
                      name="title"
                      type="text"
                      required
                      maxLength={20}
                      placeholder="一句话概括这块瓜"
                      className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  <div>
                    <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      瓜料类型
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {TOPICS.map((topic) => (
                        <label key={topic} className="cursor-pointer">
                          <input type="radio" name="topic" value={topic} className="peer sr-only" required />
                          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors peer-checked:border-primary-400 peer-checked:bg-primary-500 peer-checked:text-background-50">
                            {topic}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="content" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      投稿正文 <span className="text-foreground-400">（建议 300–1500 字）</span>
                    </label>
                    <textarea
                      id="content"
                      name="content"
                      required
                      maxLength={1500}
                      rows={8}
                      placeholder="按「标题 → 事件概述 → 详细经过 → 关键信息」的结构写下你的瓜料，信息越完整越容易过审。"
                      className="w-full resize-none rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm leading-6 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  <div>
                    <label htmlFor="evidence" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      佐证说明 <span className="text-foreground-400">（选填，文字描述即可，图片另行附件）</span>
                    </label>
                    <input
                      id="evidence"
                      name="evidence"
                      type="text"
                      placeholder="如：附相关聊天截图，敏感信息已打码"
                      className="w-full rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  <label className="flex items-start gap-2 text-sm text-foreground-700">
                    <input
                      type="checkbox"
                      name="agree"
                      required
                      className="mt-1 h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-100"
                    />
                    <span>
                      我确认本投稿为本人原创，内容真实、隐私已打码，并同意遵守平台的版权与审核规则。
                    </span>
                  </label>

                  {status === "error" && formError && (
                    <div className="flex items-start gap-2 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                      <i className="ri-error-warning-line mt-0.5"></i>
                      <span>{formError}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3.5 text-base font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i> 正在提交…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill"></i> 提交投稿
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}